import { Link } from "react-router-dom";
import { InflowLogo } from "@/components/InflowLogo";
import { PageHeader } from "@/components/PageHeader";
import { Section } from "@/components/Section";
import { PageLoadError } from "@/components/LockedPage";
import { Spinner } from "@/components/Spinner";
import { Icon } from "@/components/Icon";
import { useCourse } from "@/api/hooks";

/**
 * Курс — 6 уроків від засновника + ефір (1.0 course_*).
 *
 * Прогресія: щодня о 10:00 Київ +1 урок відкривається. До цього часу
 * показуємо 🔒. Після 6-го дня — ефір "11 способів пошуку клієнтів".
 *
 * 1.0 рендерив це як inline-keyboard в боті; у нас — повноцінна
 * сторінка Mini App з video-плеєром (поки file_id-stub, відео
 * додається пізніше через MTProto-downloader).
 */
export function CoursePage() {
  const q = useCourse();

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <PageLoadError error={q.error} section="course" />;

  const data = q.data!;

  return (
    <>
      {/* Top nav — лого зліва (єдиний хедер) + кнопка Меню справа.
          Юзер 2026-06-11: плашку «День N» прибрали — «нащо заповнювати
          це місце». Лишилась лише кнопка Меню. */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <InflowLogo size={18} to="/" />
        <Link
          to="/"
          className="flex h-8 items-center gap-1.5 rounded-full bg-surface-soft px-3 font-display text-[0.75rem] font-semibold text-ink shadow-card-soft active:scale-95 transition"
        >
          Меню
          <Icon name="home" size={14} color="#FE6B04" />
        </Link>
      </div>

      {/* Owner 2026-09-16: підзаголовок «Від засновника inFLOW» дублював
          перший рядок опису — прибрано. Тепер у підзаголовку САМ опис курсу,
          а пункти нижче — рядками в нашому стилі, а не абзацом із «•». */}
      <PageHeader
        eyebrow="навчання"
        title="Курс із продажів"
        titleLead="Курс із"
        titleAccent="продажів."
        scribbleWidth={150}
        subtitle={courseLead(data.welcome_text)}
      />

      <CourseFacts html={data.welcome_text} />

      {/* Уроки списком — курс відкритий весь одразу (юзер 2026-06-14) */}
      <Section>
        {data.items.map((lesson, i) => {
          const isLast = i === data.items.length - 1;
          return (
            <CourseLessonRow
              key={lesson.idx}
              idx={lesson.idx}
              title={lesson.title}
              isFinale={lesson.is_finale}
              viewed={lesson.viewed}
              isLast={isLast}
            />
          );
        })}
      </Section>
    </>
  );
}

/** Перший рядок опису курсу (без тегів) — ідe в підзаголовок сторінки.
 *  Текст приходить із сервера рядком HTML: «<b>лід</b><br><br>• пункт<br>…». */
function courseLead(html: string): string {
  const head = (html || "").split(/<br\s*\/?>/i)[0] ?? "";
  return head.replace(/<[^>]+>/g, "").trim();
}

/** Пункти курсу рядками в нашому стилі (owner 2026-09-16: «оформлення блока з
 *  описом не в нашому стилі»). Парсимо той самий рядок із сервера, щоб копія
 *  жила в одному місці; якщо пунктів не знайшли — показуємо текст як був. */
function CourseFacts({ html }: { html: string }) {
  const facts = (html || "")
    .split(/<br\s*\/?>/i)
    .map((x) => x.replace(/<[^>]+>/g, "").trim())
    .filter((x) => x.startsWith("•"))
    .map((x) => x.replace(/^•\s*/, ""));

  if (facts.length === 0) {
    return (
      <div className="mb-6">
        <p
          className="text-[0.875rem] leading-relaxed text-ink"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-ios-card bg-surface p-4 shadow-card">
      <div className="flex flex-col gap-2.5">
        {facts.map((f) => (
          <div key={f} className="flex items-start gap-2.5">
            {/* Крапка центрується по ПЕРШОМУ РЯДКУ тексту: обгортка має рівно
                висоту рядка (1.375em при leading-snug) і центрує флексом.
                Owner 2026-09-16: «точки по відношенню до тексту не
                центровані» — до цього стояв підібраний на око `mt-*`. */}
            <span className="flex h-[1.375em] shrink-0 items-center text-[0.8125rem]">
              {/* Замір по ФАРБІ (DPR 8): флекс центрує РЯДОК, а фарба
                  тексту сидить нижче центру рядка — крапка виходила на
                  0.81px вище. Компенсуємо константою шрифта в `em`. */}
              <span className="h-1.5 w-1.5 translate-y-[0.062em] rounded-full bg-primary" />
            </span>
            <span className="text-[0.8125rem] leading-snug text-ink-dim [text-wrap:pretty]">
              {f}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CourseLessonRow({
  idx,
  title,
  isFinale,
  viewed,
  isLast,
}: {
  idx: number;
  title: string;
  isFinale: boolean;
  viewed: boolean;
  isLast: boolean;
}) {
  // Завжди нумерація NN — інакше іконка з огоньком ефіру зміщується
  // відносно іконок уроків (★ ширша за "07").
  const indexStr = String(idx).padStart(2, "0");

  return (
    <Link to={`/course/${idx}`} className="block">
      <div
        className={`flex items-center gap-3 px-3 py-3 ${
          isLast ? "" : "border-b border-line"
        }`}
      >
        <div className="font-mono text-[0.6875rem] tabular-nums text-ink-dim">
          {indexStr}
        </div>
        <div
          className="grid h-8 w-8 place-items-center rounded-[0.625rem]"
          style={{
            background: isFinale ? "#1a1612" : "#F8F4EC",
            color: isFinale ? "#FFA135" : "#FE6B04",
          }}
        >
          <Icon name={isFinale ? "flame" : "play"} size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[0.875rem] font-semibold text-ink">
            {title}
            {isFinale && (
              <span className="ml-1.5 font-serif italic text-primary">
                ефір
              </span>
            )}
          </div>
          {/* Бейдж «Переглянуто» — після першого play відео (юзер 2026-06-14) */}
          {viewed && (
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[0.59375rem] uppercase tracking-[0.08em] text-primary">
              <Icon name="check" size={10} />
              Переглянуто
            </div>
          )}
        </div>
        <Icon name="arrow-right" size={14} color="rgba(23,20,17,0.40)" />
      </div>
    </Link>
  );
}
